import { useEffect, useState } from "react"
import styled from "styled-components"
import { Button, TextInput } from "@prosolve/common-components-ui"
import { FaPlus, FaSyncAlt } from "react-icons/fa"
import useDataAwsJobs from "../../dataHooks/useDataAwsJobs"
import useSnackbar from "../../hooks/useSnackbar"
import useMe from "../../hooks/useMe"
import ConfirmDeleteJobModal from "../Jobs/ConfirmDeleteJobModal"
import ConfirmRunJobModal from "../Jobs/ConfirmRunJobModal"
import JobFormModal from "../Jobs/JobFormModal"
import JobHistoryModal from "../Jobs/JobHistoryModal"
import { MutedText } from "../Jobs/JobTable"
import { awsIntervalUnitOptions, getJobId, toCopiedJob } from "../Jobs/jobUtils"
import AwsJobTable from "./AwsJobTable"

const Container = styled.div`
  padding: 20px;
  color: ${({ theme }) => theme.colors.text};
`

const ColumnGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const RowGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const Header = styled(RowGroup)`
  justify-content: space-between;
  flex-wrap: wrap;
`

const Title = styled.div`
  font-size: 24px;
  font-weight: bold;
`

const Group = styled.div`
  background: ${({ theme }) => theme.backgrounds.pure};
  border: ${({ theme }) => theme.borders.main};
  border-radius: ${({ theme }) => theme.borderRadius.main};
  padding: 20px;
`

const PanelTitle = styled.div`
  font-size: 18px;
  font-weight: bold;
`

export default () => {
  const [search, setSearch] = useState('')
  const [formJob, setFormJob] = useState()
  const [historyJob, setHistoryJob] = useState()
  const [deleteJob, setDeleteJob] = useState()
  const [jobToRun, setJobToRun] = useState()

  const { newMessage } = useSnackbar()
  const { hasEdit } = useMe()

  const {
    isLoading,
    jobs,
    jobHistory,
    fetchAwsJobs,
    fetchCreateAwsJob,
    fetchUpdateAwsJob,
    fetchToggleAwsJob,
    fetchDeleteAwsJob,
    fetchRunAwsJob,
    fetchAwsJobHistory
  } = useDataAwsJobs()

  useEffect(() => {
    fetchAwsJobs()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const refreshHistory = async (job = historyJob) => {
    const jobId = getJobId(job)
    if (jobId) {
      await fetchAwsJobHistory(jobId)
    }
  }

  const openHistory = async (job) => {
    setHistoryJob(job)
    await refreshHistory(job)
  }

  const openClone = (job) => {
    setFormJob(toCopiedJob(job, jobs))
  }

  const submitJob = async (payload) => {
    const jobId = getJobId(formJob)
    const savedJob = jobId
      ? await fetchUpdateAwsJob(jobId, payload)
      : await fetchCreateAwsJob(payload)

    if (savedJob) {
      newMessage({ message: jobId ? 'AWS job updated' : 'AWS job created', severity: 'success' })
      await fetchAwsJobs()
    }

    return savedJob
  }

  const toggleJob = async (job) => {
    const updatedJob = await fetchToggleAwsJob(getJobId(job), !job.enabled)

    if (updatedJob) {
      newMessage({ message: updatedJob.enabled ? 'AWS job enabled' : 'AWS job disabled', severity: 'success' })
      await fetchAwsJobs()
    }
  }

  const confirmRunJob = async () => {
    const job = jobToRun
    const result = await fetchRunAwsJob(getJobId(job))

    if (result) {
      newMessage({
        message: result.status === 'success' ? 'AWS job ran successfully' : 'AWS job run failed',
        severity: result.status === 'success' ? 'success' : 'failure'
      })
      setJobToRun()
      if (getJobId(historyJob) === getJobId(job)) {
        await refreshHistory(job)
      }
    }
  }

  const confirmDeleteJob = async () => {
    const result = await fetchDeleteAwsJob(getJobId(deleteJob))

    if (result?.success) {
      newMessage({ message: 'AWS job deleted', severity: 'success' })
      setDeleteJob()
      if (getJobId(historyJob) === getJobId(deleteJob)) {
        setHistoryJob()
      }
      await fetchAwsJobs()
    }
  }

  return (
    <Container>
      <ColumnGroup style={{ gap: '15px' }}>
        <Header>
          <ColumnGroup>
            <Title>AWS Job Manager</Title>
            <MutedText>Manage EventBridge Scheduler jobs in the portal-jobs group.</MutedText>
          </ColumnGroup>
          <RowGroup>
            <Button onClick={fetchAwsJobs}>
              <RowGroup>
                <FaSyncAlt />
                Refresh
              </RowGroup>
            </Button>
            {hasEdit &&
              <Button $colored={true} onClick={() => setFormJob({})}>
                <RowGroup>
                  <FaPlus />
                  New AWS Job
                </RowGroup>
              </Button>
            }
          </RowGroup>
        </Header>

        <Group>
          <ColumnGroup>
            <RowGroup style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <PanelTitle>AWS Jobs</PanelTitle>
              <TextInput
                value={search}
                placeholder='Search AWS jobs'
                width='260px'
                onChange={setSearch}
              />
            </RowGroup>
            <AwsJobTable
              jobs={jobs}
              search={search}
              isLoading={isLoading}
              onRun={setJobToRun}
              onHistory={openHistory}
              onEdit={setFormJob}
              onClone={openClone}
              onToggle={toggleJob}
              onDelete={setDeleteJob}
            />
          </ColumnGroup>
        </Group>
      </ColumnGroup>

      {formJob &&
        <JobFormModal
          title={formJob.isClone ? 'Clone AWS Job' : (getJobId(formJob) ? 'Edit AWS Job' : 'New AWS Job')}
          job={formJob}
          isNameDisabled={Boolean(getJobId(formJob))}
          intervalUnitOptions={awsIntervalUnitOptions}
          onClose={() => setFormJob()}
          onSubmit={submitJob}
        />
      }

      {historyJob &&
        <JobHistoryModal
          job={historyJob}
          history={jobHistory}
          isLoading={isLoading}
          onRefresh={() => refreshHistory()}
          onClose={() => setHistoryJob()}
        />
      }

      {deleteJob &&
        <ConfirmDeleteJobModal
          job={deleteJob}
          description='This will delete the EventBridge Scheduler entry from the portal-jobs schedule group.'
          onClose={() => setDeleteJob()}
          onConfirm={confirmDeleteJob}
        />
      }

      {jobToRun &&
        <ConfirmRunJobModal
          job={jobToRun}
          title='Run AWS Job'
          description='This will invoke the AWS job immediately outside of its schedule.'
          onClose={() => setJobToRun()}
          onConfirm={confirmRunJob}
        />
      }
    </Container>
  )
}
